import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";

import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AlertModal from "../../Requirement/components/common/AlerModel";
import RequirementDetailsView from "./RequirementDetailsView";
import MatchingOffers from "./MatchingOffers";
import { fetchData } from "../../Requirement/services/RequirementServices";
import { useLoading } from "../../../context/LoadingContext";
import TrustScoreModal from "../../../components/common/TrustScoreModal";

const Requirements = ({ filterType }) => {
  // Loading context
  const { setLoading } = useLoading();

  // State management
  const [requirementList, setRequirementList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowStart, setRowStart] = useState(0);
  const [productList, setProductList] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);
  const [searchValues, setSearchValues] = useState({
    RequirementNumber: "",
    searchProduct: "",
    searchProductGrade: "",
    searchQuantityRange: "",
  });
  const [productGradeList, setProductGradeList] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    autoClose: false,
  });

  // New state for requirement details and offers
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showRequirementDetails, setShowRequirementDetails] = useState(false);
  const [showMatchingOffers, setShowMatchingOffers] = useState(false);

    // State for Verification Status Modal
    const [trustModal, setTrustModal] = useState(false);

  const pageSize = 5; // Changed from 10 to 5 to match ViewOffer.js

  // Quantity options for search filter
  const quantityOptions = [
    { value: "", label: "Select Range" },
    { value: "50-100", label: "50-100 MT" },
    { value: "100-200", label: "100-200 MT" },
    { value: "200-500", label: "200-500 MT" },
    { value: "Above-500", label: "Above 500 MT" },
  ];

  // Modal handlers
  const handleModalClose = () => {
    setModal({
      show: false,
      type: "",
      title: "",
      message: "",
      autoClose: false,
    });
  };

    const trustModel = useCallback(() => {
      setTrustModal(true);
    }, []);

  // Search requirements with filter support
  const searchRequirements = useCallback(
    async (filters = {}, startRow = 0) => {
      try {
        setLoading(true);
        const filterTerms = { ...filters };

        // Add filter type support (Matched/Unmatched)
        if (filterType === "Matched") {
          filterTerms.IsMatched = 1;
        } else if (filterType === "Unmatched") {
          filterTerms.IsMatched = 0;
        }

        // Handle quantity range filtering
        if (filters.searchQuantityRange) {
          const [min, max] = filters.searchQuantityRange.split("-");
          if (min === "Above") {
            filterTerms.QuantityGT = max;
          } else {
            filterTerms.QuantityGT = min;
            filterTerms.QuantityLT = max;
          }
          delete filterTerms.searchQuantityRange;
        }
        if (filters.searchProduct) {
          filterTerms.ProductName = filters.searchProduct;
        }
        if (filters.searchProductGrade) {
          filterTerms.Grade = filters.searchProductGrade;
        }
        if (filters.requirementNumber) {
          filterTerms.RequirementNumber = filters.requirementNumber;
        }
        filterTerms.CategoryType = "requirement";

        const body = {
          target:
            filterType === "Unmatched"
              ? "listOfferUnmatchedRequirements"
              : "listOfferMatchedRequirements",
          userOrder: "CreateDate DESC",
          rowStart: startRow,
          pageSize,
          filterTerms: filterTerms,
        };
        const response = await fetchData(body);

        if (response) {
          let modifiedItems = (response.items || response).map((item) => ({
            ...item,
            id: item.RequirementsID, // Add id property with value from RequirementsID
          }));

          // Ensure newly added requirements show on top by sorting locally by creation date descending
          modifiedItems = modifiedItems.sort(
            (a, b) => new Date(b.RequirementDate) - new Date(a.RequirementDate)
          );

          setRequirementList(modifiedItems);
          setTotalItems(response.total || 0);
        }
      } catch (error) {
        console.error("Error searching requirements:", error);
        setRequirementList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [filterType, pageSize, setLoading]
  );

  // Function to refresh requirements table - can be called from child components
  const refreshRequirements = useCallback(() => {
    searchRequirements(searchValues, 0);
  }, [searchRequirements, searchValues]);

  // Load products for dropdowns and all products master list
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: { Status: "ACTIVE" },
      });

      if (response && response.items) {
        // Store all product data for grade lookup
        setAllProductMaster(response.items);

        // Create unique product list for dropdown
        const uniqueProducts = [];
        const seenProductNames = new Set();

        response.items.forEach((item) => {
          if (!seenProductNames.has(item.ProductName)) {
            seenProductNames.add(item.ProductName);
            uniqueProducts.push({
              ProductName: item.ProductName,
              ProductID: item.ProductID, // Keep first occurrence ID for reference
              ProductImagePath: item.ProductImagePath,
            });
          }
        });

        setProductList(uniqueProducts);
      } else {
        console.error(
          "Error loading products: Invalid response format",
          response
        );
        setProductList([]);
        setAllProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products:", error);
      setProductList([]);
      setAllProductMaster([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Load product grades based on selected product from stored data
  const loadProductGrades = useCallback(
    (productName) => {
      if (!productName || !allProductMaster.length) {
        setProductGradeList([]);
        return;
      }

      // Filter grades for the selected product from stored data
      const productGrades = allProductMaster
        .filter((item) => item.ProductName === productName)
        .map((item) => ({
          Grade: item.Grade,
          ProductID: item.ProductID,
        }))
        .filter(
          (grade, index, self) =>
            // Remove duplicate grades
            index === self.findIndex((g) => g.Grade === grade.Grade)
        );

      setProductGradeList(productGrades);
    },
    [allProductMaster]
  );

  // Handle search
  const handleSearch = (searchValues) => {
    setRowStart(0);
    setCurrentPage(1);
    searchRequirements(searchValues, 0);
  };

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      console.log("Page changed to:", page);
      setCurrentPage(page);
      const newRowStart = (page - 1) * pageSize;
      setRowStart(newRowStart);
      searchRequirements(searchValues, newRowStart);
    },
    [pageSize, searchRequirements, searchValues]
  );

  // Handle row click to show requirement details and offers
  const handleRowClick = (requirement) => {
    console.log("Requirement selected:", requirement);
    setSelectedRequirement(requirement);
    setShowRequirementDetails(true);
    setShowMatchingOffers(true);
  };

  // Get buyer information

  // Search filter fields configuration
  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, RequirementNumber: value })),
      value: searchValues.RequirementNumber,
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProduct: value }));
        loadProductGrades(value);
      },
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
    },
    {
      name: "searchQuantityRange",
      label: "Quantity Range (MT)",
      type: "dropdown",
      options: quantityOptions,
      value: searchValues.searchQuantityRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchQuantityRange: value }));
        searchRequirements({ ...searchValues, searchQuantityRange: value }, 0);
      },
    },
  ];

  // Table columns configuration
  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Req No." },
    {
      id: "Product",
      numeric: false,
      label: "Product",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.ProductName : "";
      },
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.Grade : "";
      },
    },
    { id: "Quantity", numeric: true, label: "Quantity(MT)" },
    { id: "OriginCountry", numeric: false, label: "Origin" },
    {
      id: "DeliveryLocation",
      numeric: false,
      label: "Destination",
      renderCell: (row) => {
        let res = row.DeliveryLocation?.split("~");
        if (Array.isArray(res)) {
          return res[0];
        }
        return "";
      },
    },
    {
      id: "ShipmentPeriod",
      numeric: false,
      label: "Shipment Period",
      renderCell: (row) => {
        let dpFrom = row.DeliveryPeriodFrom
          ? dayjs(row.DeliveryPeriodFrom).format("DD-MM-YYYY")
          : "";
        let dpTill = row.DeliveryPeriodTill
          ? dayjs(row.DeliveryPeriodTill).format("DD-MM-YYYY")
          : "";
        return dpFrom !== "" && dpTill !== "" ? dpFrom + " to " + dpTill : "";
      },
    },
    {
      id: "DeliveryPort",
      numeric: false,
      label: "Delivery Port",
      renderCell: (row) => {
        let res = row.DeliveryLocation?.split("~");
        if (Array.isArray(res) && res.length > 0) {
          return res[1];
        }
        return "";
      },
    },
    { id: "Incoterm", numeric: false, label: "Incoterms" },
    {
      id: "ProductPackaging",
      numeric: false,
      label: "Packaging",
      renderCell: (row) => {
        if (!row.ProductPackaging) return "";
        try {
          const jsonLike = row.ProductPackaging.replace(/{|}/g, "")
            .split(",")
            .map((part) => part.split("="))
            .reduce((obj, [key, value]) => {
              obj[key?.trim()] = value ? value.trim() : "";
              return obj;
            }, {});
          return jsonLike.item || "";
        } catch (error) {
          return "";
        }
      },
    },
    {
      id: "RequirementDate",
      numeric: false,
      label: "Created",
      renderCell: (row) =>
        row.RequirementDate
          ? dayjs(row.RequirementDate).format("DD-MM-YYYY")
          : "",
    },
    {
      id: "ValidUntil",
      numeric: false,
      label: "Expires",
      renderCell: (row) =>
        row.ValidUntil ? dayjs(row.ValidUntil).format("DD-MM-YYYY") : "",
    },
    {
      id: "CreatedByEmailID",
      numeric: false,
      label: "Created By",
      renderCell: (row) => row.CreatedByEmailID || "",
    },
    {
      id: "KYBStatus",
      numeric: false,
      label: "Buyer Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.KYBStatus) {
          case "Approved":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
              display: "inline-block",
            }}
          >
            {row.KYBStatus === "Approved" ? "Verified" : "Unverified"}
          </span>
        );
      },
    },
  ];

  // Load products on component mount
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    console.log("Requirements filter changed:", filterType);
    // Reset to first page when filter changes
    setCurrentPage(1);
    setRowStart(0);

    // Clear selected requirement and hide details when switching tabs
    setSelectedRequirement(null);
    setShowRequirementDetails(false);
    setShowMatchingOffers(false);

    searchRequirements(searchValues, 0);
  }, [filterType, allProductMaster.length, searchValues, searchRequirements]);

  return (
    <div>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Requirements ({filterType})</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={requirementList}
        total={totalItems}
        title="Requirements Information"
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        currentPage={currentPage}
        onRowClick={handleRowClick}
        selectedRows={
          selectedRequirement
            ? [selectedRequirement.RequirementsID || selectedRequirement.id]
            : []
        }
      />

      {/* Requirement Details Form */}
      {showRequirementDetails && selectedRequirement && (
        <div className="row mt-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Requirement Details</h5>
                <div className="trust-score">
                <button
                    className="submit-btn-more"
                    onClick={trustModel}
                    style={{ marginRight: "10px" }}
                  >
                    Verification Status
                  </button>
                <button
                  className="submit-btn-more"
                  onClick={() => {
                    setShowRequirementDetails(false);
                    setShowMatchingOffers(false);
                    setSelectedRequirement(null);
                  }}
                >
                  Close
                </button>
                </div>
              </div>
              <div className="card-body">
                <RequirementDetailsView
                  requirement={selectedRequirement}
                  productList={productList}
                  allProductMaster={allProductMaster}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matching Offers */}
      {showMatchingOffers && selectedRequirement && (
        <div className="row mt-4">
          <div className="col-md-12">
            <div className="card1">
              <div className="card-header">
                <h5 className="mb-0">
                  {filterType === "Unmatched"
                    ? `Create New Offer for Requirement #${selectedRequirement.RequirementNumber}`
                    : `Matched Offers for Requirement #${selectedRequirement.RequirementNumber}`}
                </h5>
              </div>
              <div className="card-body">
                <MatchingOffers
                  requirementDetails={selectedRequirement}
                  allProductMaster={allProductMaster}
                  filterType={filterType}
                  onRequirementsRefresh={refreshRequirements}
                  onDeselectRequirement={() => setSelectedRequirement(null)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Verification Status Modal */}
      {trustModal && (
        <TrustScoreModal
          onClose={() => setTrustModal(false)}
          userEmail={selectedRequirement.CreatedByEmailID}
        />
      )}
    </div>
  );
};

export default Requirements;
